// Astrea Ops Platform - real Supabase Auth wrapper
//
// Phase 3, users slice. Replaces the old fully client-side `u.password!==pass`
// check (a plaintext comparison against the in-memory `users` array) with real
// Supabase Auth. Per Martin's 15 September decision to go with the full deployment:
// accounts are created/reset only through the admin-gated grantAccess() call below,
// which hits the new /api/create-user-account.js serverless function with a
// server-generated temporary password - there is no self-service sign-up anywhere
// in this app.

import { supabase } from './supabaseClient';

export async function signIn(email, password) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  return data;
}

export async function signOut() {
  await supabase.auth.signOut();
}

// Verifies the current password by re-authenticating with it (Supabase Auth has no
// "check password without a session change" call), then sets the new one and clears
// the forced-reset flag. Throws with a message ChangePasswordModal can show directly.
export async function changePassword(email, currentPassword, newPassword, profileId) {
  const { error: verifyError } = await supabase.auth.signInWithPassword({ email, password: currentPassword });
  if (verifyError) throw new Error('Current password is incorrect.');
  // v7.71 (11 Oct 2026): Supabase's "Require current password when updating" is on, so the
  // current password goes with the change; without it the server refuses every change,
  // including the forced first-sign-in change for a new or reset login.
  const { error: updateError } = await supabase.auth.updateUser({ password: newPassword, current_password: currentPassword });
  if (updateError) throw updateError;
  if (profileId) {
    const { error: profileError } = await supabase
      .from('profiles')
      .update({ must_reset_password: false, password_changed_at: new Date().toISOString() })
      .eq('id', profileId);
    if (profileError) throw profileError;
  }
}

// Admin-only: create a brand-new login for someone who doesn't have one yet, or
// reset an existing one's password. Calls the serverless endpoint (never the
// Supabase Auth Admin API directly - that needs the service-role key, which must
// never reach the browser bundle) with the caller's own access token, so the
// endpoint can verify admin status itself rather than trusting the client.
// Returns { email, tempPassword } - the caller is responsible for showing the
// temp password once and never persisting it anywhere.
export async function grantAccess(personId, role) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not signed in.');
  const res = await fetch('/api/create-user-account', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
    body: JSON.stringify({ personId, role }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || 'Failed to grant access.');
  return body;
}

// System-Admin-only: grant or revoke another user's System Admin flag. Plain RLS-
// gated Supabase update, not the serverless endpoint - this never touches the Auth
// Admin API or a password, so it doesn't need the service-role key. The database is
// the real enforcement here (profiles_system_admin_update policy + the
// trg_guard_is_system_admin_change trigger, both added in
// 20260919000001_senior_management_and_system_admin.sql): a caller who isn't
// already a System Admin gets a Postgres error back from this call regardless of
// what the UI does or doesn't show them, which is the point.
export async function setSystemAdmin(profileId, isSystemAdmin) {
  const { error } = await supabase.from('profiles').update({ is_system_admin: isSystemAdmin }).eq('id', profileId);
  if (error) throw error;
}
